// src/polaris-components/AthleteDirectoryPage.tsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import AthleteCard, { Athlete } from "./AthleteCard";
import AddAthleteProfileModal from "./AddAthleteProfileModal";
import DeleteAthleteModal from "./DeleteAthleteModal";
import AddTaskModal from "./AddTaskModal";
import ProvisionStudentModal from "./ProvisionStudentModal";
import BulkProvisionAthletesModal from "./BulkProvisionAthletesModal";
import type { AthleticOutlookAthlete } from "./AthleticOutlookModal";
import { buildApiUrl } from "../utils/apiConfig";
import { fetchCompanyAthletesApi } from "../services/athletesService";
import { CompanyAthleteResponse } from "../types/athletes";
import { resolveGuardianEmails } from "../utils/guardianEmails";
import type { AthleteSaveMeta } from "./AddAthleteProfileForm";
import { canManageAthletes, getUserRole, isCoach } from "../utils/auth";
import { getAuthInternalCoachPreReadEnabled } from "../utils/authStore";
import { useSubscriptionLimits } from "../hooks/useSubscriptionLimits";
import SubscriptionLimitModal from "./SubscriptionLimitModal";
import { useAuth } from "../contexts/AuthContext";
import { parseApiErrorMessage } from "../utils/apiErrorMessage";
import "../styles/athlete-directory.css";
import "../styles/team-page.css";
import searchIcon from "../images/Search.svg";

const AthleticOutlookModal = React.lazy(() => import("./AthleticOutlookModal"));

type ListResponse =
    | Athlete[]
    | { rows?: Athlete[]; total?: number }
    | { athletes?: Athlete[]; total?: number }
    | { content?: Athlete[]; totalElements?: number; totalPages?: number };

function useDebounced<T>(value: T, delay = 350): T {
    const [v, setV] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setV(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return v;
}

// --- Type guards to narrow union shapes ---
function hasRows(x: unknown): x is { rows?: Athlete[]; total?: number } {
    return !!x && typeof x === "object" && "rows" in (x as any);
}
function hasAthletes(x: unknown): x is { athletes?: Athlete[]; total?: number } {
    return !!x && typeof x === "object" && "athletes" in (x as any);
}
function hasContent(x: unknown): x is { content?: Athlete[]; totalElements?: number; totalPages?: number } {
    return !!x && typeof x === "object" && "content" in (x as any);
}

export default function AthleteDirectoryPage() {
    const [athletes, setAthletes] = useState<Athlete[]>([]);
    const [total, setTotal] = useState<number | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [q, setQ] = useState("");
    const [showAddAthleteModal, setShowAddAthleteModal] = useState(false);
    const [showProvisionStudentModal, setShowProvisionStudentModal] = useState(false);
    const [showBulkProvisionModal, setShowBulkProvisionModal] = useState(false);
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedAthlete, setSelectedAthlete] = useState<Athlete | null>(null);
    const [athleteToDelete, setAthleteToDelete] = useState<Athlete | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [outlookAthlete, setOutlookAthlete] = useState<AthleticOutlookAthlete | null>(null);
    const [outlookStartInEdit, setOutlookStartInEdit] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const dq = useDebounced(q, 400);
    const hasFetchedRef = useRef(false);

    const { limits, refetch: refetchLimits } = useSubscriptionLimits();
    const { authProvider } = useAuth();
    const isCleverUser = authProvider === "clever";

    const userRole = getUserRole();
    const isStaffOrAdmin = useMemo(() => {
        if (!userRole) return false;
        const lowerRole = userRole.toLowerCase();
        return lowerRole === "staff" ||
               lowerRole === "admin" ||
               lowerRole === "administrator" ||
               lowerRole.includes("staff") ||
               lowerRole.includes("admin");
    }, [userRole]);

    const isCleverStaffOrAdmin = isCleverUser && isStaffOrAdmin;
    const showBulkUpload = (isStaffOrAdmin && !isCleverUser) || isCleverStaffOrAdmin;
    const canManageAthletesOnPage = canManageAthletes();

    const authHeaders = useMemo(() => {
        return { Accept: "application/json" };
    }, []);

function toStaffProfileId(id: string | number | null | undefined): number | undefined {
    if (id == null || id === "") return undefined;
    const parsed = typeof id === "number" ? id : Number(id);
    return Number.isFinite(parsed) ? parsed : undefined;
}

    const mapCompanyAthleteToAthlete = useCallback((ca: CompanyAthleteResponse): Athlete => {
        const guardianEmails = resolveGuardianEmails(ca);
        return {
        id: ca.id,
        userId: ca.userId,
        firstName: ca.firstName,
        lastName: ca.lastName,
        email: ca.email,
        phoneNumber: ca.phoneNumber || undefined,
        lastLoginTime: ca.lastLoginTime || undefined,
        parentEmail: guardianEmails.parentEmail || undefined,
        parentEmail2: guardianEmails.parentEmail2 || undefined,
        assignedStaffName: ca.assignedStaffName || undefined,
        assignedStaffId: toStaffProfileId(ca.assignedStaffId),
        location: ca.location || undefined,
        athleteProfileId: ca.athleteProfileId,
        athleteId: ca.athleteProfileId ?? ca.id,
        imageUrl: ca.profilePictureUrl || undefined,
        guardians: ca.guardians || [],
        hasAthleticOutlook: Boolean(ca.hasAthleticOutlook),
        canEditAthleticOutlook: Boolean(ca.canEditAthleticOutlook),
        canCreateAthleticOutlook: Boolean(ca.canCreateAthleticOutlook),
    };
    }, []);

    const fetchCompanyAthletes = useCallback(async (opts?: { searchQuery?: string; silent?: boolean; page?: number }) => {
        const silent = opts?.silent ?? false;
        const searchQuery = opts?.searchQuery;
        const page = opts?.page ?? currentPage;
        if (!silent) {
            setLoading(true);
        }
        setError(null);
        try {
            const response = await fetchCompanyAthletesApi({
                page,
                size: pageSize,
                sort: "id",
                query: searchQuery?.trim() || undefined
            });

            const mappedAthletes = response.content.map(mapCompanyAthleteToAthlete);
            setAthletes(mappedAthletes);
            setTotal(response.totalElements);
            setTotalPages(response.totalPages);
        } catch (e: any) {
            // Check if error is 401 Unauthorized
            if (e?.response?.status === 401 || e?.message?.includes('401') || e?.status === 401) {
                window.location.href = '/login';
                return;
            }
            setError(e?.message || "Failed to load athletes");
            setAthletes([]);
            setTotal(undefined);
            setTotalPages(0);
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [currentPage, pageSize, mapCompanyAthleteToAthlete]);

    const fetchList = useCallback(async (endpoint: string, opts?: { silent?: boolean }) => {
        const silent = opts?.silent ?? false;
        if (!silent) {
            setLoading(true);
        }
        setError(null);
        try {
            const res = await fetch(endpoint, {
                headers: authHeaders,
                credentials: "include" // Use HTTP-only cookie for auth
            });

            if (res.status === 401) {
                // Redirect to login page when unauthorized
                window.location.href = '/login';
                return;
            }

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json: ListResponse = await res.json();

            let rows: Athlete[] = [];
            let tot: number | undefined;

            if (Array.isArray(json)) {
                rows = json;
                tot = json.length;
            } else if (hasContent(json)) {
                // Handle paginated response format (Spring Boot style)
                rows = json.content ?? [];
                tot = json.totalElements;
                setTotalPages(json.totalPages ?? 0);
            } else if (hasRows(json)) {
                rows = json.rows ?? [];
                tot = json.total;
            } else if (hasAthletes(json)) {
                rows = json.athletes ?? [];
                tot = json.total;
            }

            setAthletes(rows);
            setTotal(typeof tot === "number" ? tot : rows.length);
        } catch (e: any) {
            setError(e?.message || "Failed to load athletes");
            setAthletes([]);
            setTotal(undefined);
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [authHeaders]);

    // Reset to page 0 when search query changes
    useEffect(() => {
        setCurrentPage(0);
        hasFetchedRef.current = false;
    }, [dq]);

    // Single effect to handle all data fetching - initial load, search, and pagination
    useEffect(() => {
        // Prevent duplicate calls in StrictMode during initial mount
        if (!hasFetchedRef.current && currentPage === 0 && !dq.trim()) {
            hasFetchedRef.current = true;
        }

        if (!dq.trim()) {
            if (isStaffOrAdmin) {
                fetchCompanyAthletes();
            } else {
                fetchList(buildApiUrl("api/athletes"));
            }
        } else {
            fetchList(buildApiUrl(`api/athletes/search?query=${encodeURIComponent(dq.trim())}&page=${currentPage}&size=${pageSize}&sort=id`));
        }
    }, [dq, currentPage, pageSize, isStaffOrAdmin, fetchCompanyAthletes, fetchList]);

    const sortAthletesByProfileId = (rows: Athlete[]) =>
        [...rows].sort(
            (a, b) =>
                Number(a.id ?? a.athleteProfileId ?? 0) - Number(b.id ?? b.athleteProfileId ?? 0)
        );

    const withCreateOutlookFallback = (row: Athlete): Athlete => {
        if (row.hasAthleticOutlook || row.canCreateAthleticOutlook) {
            return row;
        }
        if (isCoach() || getAuthInternalCoachPreReadEnabled()) {
            return { ...row, canCreateAthleticOutlook: true };
        }
        return row;
    };

    const handleAddAthleteSuccess = (_saved?: unknown, meta?: AthleteSaveMeta) => {
        refetchLimits();
        const ca = meta?.companyAthlete;
        if (ca && !dq.trim()) {
            const row = withCreateOutlookFallback(mapCompanyAthleteToAthlete(ca));
            const rowKey = row.athleteProfileId ?? row.id;

            if (isStaffOrAdmin) {
                const newTotal = (total ?? 0) + 1;
                const pageOfNewAthlete = Math.floor((newTotal - 1) / pageSize);
                setTotal(newTotal);
                setTotalPages(Math.max(1, Math.ceil(newTotal / pageSize)));
                if (pageOfNewAthlete === currentPage) {
                    setAthletes((prev) => {
                        if (prev.some((a) => (a.athleteProfileId ?? a.id) === rowKey)) {
                            return prev;
                        }
                        return sortAthletesByProfileId([...prev, row]);
                    });
                }
                return;
            }

            setTotal((t) => (t ?? 0) + 1);
            setAthletes((prev) => {
                if (prev.some((a) => (a.athleteProfileId ?? a.id) === rowKey)) {
                    return prev;
                }
                return sortAthletesByProfileId([...prev, row]);
            });
            return;
        }

        if (!dq.trim()) {
            if (isStaffOrAdmin) {
                fetchCompanyAthletes({ silent: true });
            } else {
                fetchList(buildApiUrl("api/athletes"), { silent: true });
            }
        } else {
            fetchList(
                buildApiUrl(
                    `api/athletes/search?query=${encodeURIComponent(dq.trim())}&page=${currentPage}&size=${pageSize}&sort=id`
                ),
                { silent: true }
            );
        }
    };

    /** After bulk upload: refetch limits and reload the grid so new athletes appear (page 0 for company list). */
    const handleBulkProvisionComplete = useCallback(async () => {
        await refetchLimits();
        if (!dq.trim()) {
            if (isStaffOrAdmin) {
                setCurrentPage(0);
                await fetchCompanyAthletes({ silent: true, page: 0 });
            } else {
                setCurrentPage(0);
                await fetchList(buildApiUrl("api/athletes"), { silent: true });
            }
        } else {
            setCurrentPage(0);
            await fetchList(
                buildApiUrl(
                    `api/athletes/search?query=${encodeURIComponent(dq.trim())}&page=0&size=${pageSize}&sort=id`
                ),
                { silent: true }
            );
        }
    }, [refetchLimits, dq, isStaffOrAdmin, fetchCompanyAthletes, fetchList, pageSize]);

    const handleAddAthleteClick = async () => {
        // Fetch fresh limits before checking to ensure accuracy
        try {
            const { fetchSubscriptionLimits, isAtAthleteLimit } = await import('../services/subscriptionLimitsService');
            const freshLimits = await fetchSubscriptionLimits();

            // Check if at limit
            if (isAtAthleteLimit(freshLimits)) {
                setShowLimitModal(true);
                return;
            }

            // Under limit - proceed with adding athlete
            setShowAddAthleteModal(true);

            // Also refetch the hook state for UI updates
            await refetchLimits();
        } catch (error) {
            // If check fails, show limit modal to be safe
            setShowLimitModal(true);
        }
    };

    const handleProvisionStudentClick = async () => {
        try {
            const { fetchSubscriptionLimits, isAtAthleteLimit } = await import('../services/subscriptionLimitsService');
            const freshLimits = await fetchSubscriptionLimits();
            if (isAtAthleteLimit(freshLimits)) {
                setShowLimitModal(true);
                return;
            }
            setShowProvisionStudentModal(true);
            await refetchLimits();
        } catch {
            setShowLimitModal(true);
        }
    };

    const handleBulkProvisionClick = async () => {
        try {
            const { fetchSubscriptionLimits, isAtAthleteLimit } = await import('../services/subscriptionLimitsService');
            const freshLimits = await fetchSubscriptionLimits();
            if (isAtAthleteLimit(freshLimits)) {
                setShowLimitModal(true);
                return;
            }
            setShowBulkProvisionModal(true);
            await refetchLimits();
        } catch {
            setShowLimitModal(true);
        }
    };

    const toOutlookAthlete = (athlete: Athlete): AthleticOutlookAthlete | null => {
        const athleteId = Number(athlete.athleteId ?? athlete.athleteProfileId ?? athlete.id);
        if (!Number.isFinite(athleteId) || athleteId <= 0) {
            return null;
        }
        return {
            athleteId,
            firstName: athlete.firstName,
            lastName: athlete.lastName,
            sportName: athlete.sportName,
            imageUrl: athlete.profilePictureUrl || athlete.imageUrl,
        };
    };

    const handlePreread = (athlete: Athlete, startInEdit: boolean) => {
        const target = toOutlookAthlete(athlete);
        if (!target) return;
        setOutlookStartInEdit(startInEdit);
        setOutlookAthlete(target);
    };

    const refreshDirectory = useCallback(() => {
        if (!dq.trim()) {
            if (isStaffOrAdmin) {
                fetchCompanyAthletes({ silent: true });
            } else {
                fetchList(buildApiUrl("api/athletes"), { silent: true });
            }
        } else {
            fetchList(
                buildApiUrl(
                    `api/athletes/search?query=${encodeURIComponent(dq.trim())}&page=${currentPage}&size=${pageSize}&sort=id`
                ),
                { silent: true }
            );
        }
    }, [dq, isStaffOrAdmin, fetchCompanyAthletes, fetchList, currentPage, pageSize]);

    const handleEditAthlete = (athlete: Athlete) => {
        setSelectedAthlete(athlete);
        setShowEditModal(true);
    };

    const handleEditSuccess = () => {
        if (!dq.trim()) {
            if (isStaffOrAdmin) {
                fetchCompanyAthletes();
            } else {
                fetchList(buildApiUrl("api/athletes"));
            }
        } else {
            fetchList(buildApiUrl(`api/athletes/search?query=${encodeURIComponent(dq.trim())}&page=${currentPage}&size=${pageSize}&sort=id`));
        }
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedAthlete(null);
    };

    const handleDeleteAthlete = (athlete: Athlete) => {
        setDeleteError(null);
        setAthleteToDelete(athlete);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!athleteToDelete) return;

        setDeleteLoading(true);
        setDeleteError(null);

        try {
            const athleteId = athleteToDelete.id || athleteToDelete.athleteProfileId;

            if (!athleteId) {
                throw new Error("Athlete ID not found");
            }

            const response = await fetch(buildApiUrl(`api/athletes/${athleteId}`), {
                method: "DELETE",
                headers: { Accept: "application/json" },
                credentials: "include"
            });

            if (response.status === 401) {
                window.location.href = '/login';
                return;
            }

            if (!response.ok) {
                const message = await parseApiErrorMessage(
                    response,
                    "Failed to delete athlete. Please try again.",
                );
                setDeleteError(message);
                return;
            }

            if (!dq.trim()) {
                if (isStaffOrAdmin) {
                    fetchCompanyAthletes({ silent: true });
                } else {
                    fetchList(buildApiUrl("api/athletes"));
                }
            } else {
                fetchList(buildApiUrl(`api/athletes/search?query=${encodeURIComponent(dq.trim())}&page=${currentPage}&size=${pageSize}&sort=id`));
            }

            refetchLimits();

            setShowDeleteModal(false);
            setAthleteToDelete(null);
            setDeleteError(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to delete athlete. Please try again.";
            setDeleteError(message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setAthleteToDelete(null);
        setDeleteError(null);
        setDeleteLoading(false);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(0); // Reset to first page when changing page size
    };

    const canGoPrevious = currentPage > 0;
    const canGoNext = currentPage < totalPages - 1;
    const showPagination = isStaffOrAdmin && !loading && !error && athletes.length > 0;

    // Generate page numbers with ellipsis
    const getPageNumbers = () => {
        const pages: (number | 'ellipsis')[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible + 2) {
            // Show all pages if total is small
            for (let i = 0; i < totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(0);

            if (currentPage <= 2) {
                // Near start: 1, 2, 3, 4, ..., last
                for (let i = 1; i <= 3; i++) {
                    pages.push(i);
                }
                pages.push('ellipsis');
                pages.push(totalPages - 1);
            } else if (currentPage >= totalPages - 3) {
                // Near end: 1, ..., last-3, last-2, last-1, last
                pages.push('ellipsis');
                for (let i = totalPages - 4; i < totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // Middle: 1, ..., current-1, current, current+1, ..., last
                pages.push('ellipsis');
                pages.push(currentPage - 1);
                pages.push(currentPage);
                pages.push(currentPage + 1);
                pages.push('ellipsis');
                pages.push(totalPages - 1);
            }
        }

        return pages;
    };

    return (
        <section className="ad-page page-container" aria-busy={loading}>
            <div className="ad-topbar">
                <div className="ad-searchWrap">
                    <div className="search-icon">
                        <img src={searchIcon} alt="Search Icon" />
                    </div>
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="ad-searchInput"
                        placeholder="Search"
                        aria-label="Search athletes"
                    />
                </div>
                <div className="ad-buttons">
                    <button
                        className="btn btn-red"
                        type="button"
                        onClick={() => setShowAddTaskModal(true)}
                    >
                        <span className="icon" aria-hidden>+</span>
                        <span className="text">Add task</span>
                    </button>
                    {!isCleverUser && canManageAthletesOnPage && (
                        <button
                            onClick={handleAddAthleteClick}
                            className="btn ad-addBtn"
                            type="button"
                        >
                            <span className="ad-plus" aria-hidden>＋</span>
                            <span>Add athlete</span>
                        </button>
                    )}
                    {isCleverStaffOrAdmin && (
                        <button
                            onClick={handleProvisionStudentClick}
                            className="btn ad-addBtn"
                            type="button"
                        >
                            <span className="ad-plus" aria-hidden>＋</span>
                            <span>Provision student</span>
                        </button>
                    )}
                    {showBulkUpload && (
                        <button
                            onClick={handleBulkProvisionClick}
                            className="btn ad-addBtn"
                            type="button"
                        >
                            <span className="ad-plus" aria-hidden>＋</span>
                            <span>Bulk upload</span>
                        </button>
                    )}
                </div>
            </div>
            {typeof total === "number" && <div className="ad-count-row"><span className="ad-count">{total} students</span></div>}

            {loading && <div className="ad-spinner" aria-live="polite">Loading…</div>}
            {error && !loading && <div className="ad-alert ad-alert-error">{error}</div>}

            {!loading && !error && athletes.length === 0 && !dq.trim() && (
                <div className="ad-empty">
                    <div className="ad-emptyIllustration" aria-hidden="true">
                        <div className="ad-emptyPlus">+</div>
                    </div>
                    <div className="ad-emptyTitle">No Athletes Yet</div>
                    <p className="ad-emptySub">
                        Once you add your first athlete, you'll be able to collaborate and start making progress together.
                    </p>
                    <div className="ad-emptyActions">
                        {!isCleverUser && canManageAthletesOnPage && (
                            <button
                                onClick={handleAddAthleteClick}
                                className="btn ad-addBtn"
                                type="button"
                            >
                                <span className="ad-plus" aria-hidden>＋</span> Add athlete
                            </button>
                        )}
                        {isCleverStaffOrAdmin && (
                            <button
                                onClick={handleProvisionStudentClick}
                                className="btn ad-addBtn"
                                type="button"
                            >
                                <span className="ad-plus" aria-hidden>＋</span> Provision student
                            </button>
                        )}
                        {showBulkUpload && (
                            <button
                                onClick={handleBulkProvisionClick}
                                className="btn ad-addBtn"
                                type="button"
                            >
                                <span className="ad-plus" aria-hidden>＋</span> Bulk upload
                            </button>
                        )}
                    </div>
                </div>
            )}

            {!loading && !error && athletes.length === 0 && dq.trim() && (
                <div className="ad-empty">
                    <div className="ad-emptyTitle">No athletes found</div>
                    <p className="ad-emptySub">
                        Try adjusting your search terms or browse all athletes.
                    </p>
                </div>
            )}

            {!loading && !error && athletes.length > 0 && (
                <div className="ad-list" data-testid="athlete-directory-list">
                    {athletes.map((a) => (
                        <AthleteCard
                            key={a.id ?? `${a.email}-${a.firstName}-${a.lastName}`}
                            athlete={a}
                            onEdit={canManageAthletesOnPage ? handleEditAthlete : undefined}
                            onDelete={canManageAthletesOnPage ? handleDeleteAthlete : undefined}
                            onPreread={handlePreread}
                        />
                    ))}
                </div>
            )}

            {showPagination && (
                <div className="ad-pagination" data-testid="athlete-pagination">
                    <div className="ad-pagination-info">
                        <span>
                            Showing <strong>{currentPage * pageSize + 1}-{Math.min(currentPage * pageSize + athletes.length, total || 0)}</strong> of <strong>{total || 0}</strong> students
                        </span>
                    </div>

                    <div className="ad-pagination-controls">
                        <div className="ad-page-nav">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={!canGoPrevious}
                                className="ad-page-btn"
                                aria-label="Previous page"
                                data-testid="pagination-previous"
                            >
                                Previous
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === 'ellipsis') {
                                    return (
                                        <span key={`ellipsis-${index}`} className="ad-page-ellipsis">
                                            ...
                                        </span>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`ad-page-number ${page === currentPage ? 'ad-page-number-active' : ''}`}
                                        aria-label={`Page ${page + 1}`}
                                        aria-current={page === currentPage ? 'page' : undefined}
                                        data-testid={`page-number-${page + 1}`}
                                    >
                                        {page + 1}
                                    </button>
                                );
                            })}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={!canGoNext}
                                className="ad-page-btn"
                                aria-label="Next page"
                                data-testid="pagination-next"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AddAthleteProfileModal
                open={showAddAthleteModal}
                onClose={() => setShowAddAthleteModal(false)}
                onSaved={handleAddAthleteSuccess}
            />

            <AddAthleteProfileModal
                open={showEditModal}
                onClose={handleCloseEditModal}
                onSaved={handleEditSuccess}
                initial={selectedAthlete || undefined}
            />

            <SubscriptionLimitModal
                open={showLimitModal}
                onClose={() => setShowLimitModal(false)}
                limits={limits}
            />

            <DeleteAthleteModal
                open={showDeleteModal}
                athleteName={`${athleteToDelete?.firstName || ''} ${athleteToDelete?.lastName || ''}`.trim()}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                loading={deleteLoading}
                error={deleteError ?? undefined}
            />

            <AddTaskModal
                open={showAddTaskModal}
                onClose={() => setShowAddTaskModal(false)}
            />

            <ProvisionStudentModal
                open={showProvisionStudentModal}
                onClose={() => setShowProvisionStudentModal(false)}
                onProvisioned={handleAddAthleteSuccess}
                onAthleteLimitReached={() => {
                    setShowProvisionStudentModal(false);
                    setShowLimitModal(true);
                }}
            />

            <BulkProvisionAthletesModal
                open={showBulkProvisionModal}
                onClose={() => setShowBulkProvisionModal(false)}
                onProvisioned={handleBulkProvisionComplete}
            />

            {outlookAthlete && (
                <React.Suspense fallback={null}>
                    <AthleticOutlookModal
                        open
                        athlete={outlookAthlete}
                        startInEdit={outlookStartInEdit}
                        onClose={() => setOutlookAthlete(null)}
                        onSaved={refreshDirectory}
                    />
                </React.Suspense>
            )}
        </section>
    );
}